import { Injectable, Inject, Optional, Injector } from '@angular/core';
import { UIRouter } from '@uirouter/angular';
import { Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { flatten } from 'lodash';
import { AppProfileService } from '@CaseOne/App/src/profile/shared/services/app-profile.service';
import { CommonAppDataService } from '@CaseOne/Common/app_data/app_data.service';
import { CommonBaseService } from '@CaseOne/Common/common-base-service.class/common-base-service.class';
import { CommonCheckInactiveService } from '@CaseOne/Common/common-check-inactive/services/common-check-inactive.service';
import { COMMON_USER_UNAUTHORIZED_EVENT } from '@CaseOne/Common/constants/common-user-unauthorized.constant';
import { CommonEventsService } from '@CaseOne/Common/events/services/events.service';
import { CommonModulesPlaceholders } from '@CaseOne/Common/modules_placeholders/modules_placeholders.service';
import { CommonNotificationService } from '@CaseOne/Common/notification/notification.service';
import { setCustomData, getLeaders, getMainLeader } from '@CaseOne/Common/utilities/leadershipper';
import { cancelTask, timeoutByIndexBD } from '@CaseOne/Common/utilities/timeout-without-setTimeout';
import { CommonWebSocketConnection } from '@CaseOne/Common/web-socket-connection.service/web-socket-connection.service';
import {
	CommonUserNotificationDialogService,
} from '@CaseOne/Common/user-notification-dialog/services/user-notification-dialog.service';
import { ICommonUserNotificationDialog } from '@CaseOne/Common/user-notification-dialog/types/user-notification-dialog.interface';
import { CommonWindowService } from '@CaseOne/Common/window/services/common-window.service';
import { COMMON_APP_CONFIG, ICommonAppConfig } from './app.config.token';

/**
 * FIXME: Remove after migrating to the native Angular Router.
 */

@Injectable({
	providedIn: 'root',
})
export class CommonAppRunService extends CommonBaseService {
	private signalRSubscriptions: Subscription[] = [];
	private leaderCheckTimer: any;
	public isInitialized = false;

	constructor(
		@Inject(COMMON_APP_CONFIG) private appConfig: ICommonAppConfig,
		@Optional() private uiRouter: UIRouter,
		public injector: Injector,
		private commonWebSocketConnection: CommonWebSocketConnection,
		private appData: CommonAppDataService,
		private notificationService: CommonNotificationService,
		private commonCheckInactiveService: CommonCheckInactiveService,
		private commonWindowService: CommonWindowService,
		private commonEventsService: CommonEventsService,
		private commonProfileService: AppProfileService,
		private commonModulesPlaceholders: CommonModulesPlaceholders,
		private commonUserNotificationDialogService: CommonUserNotificationDialogService,
	) {
		super(injector);
	}

	private getRouter(): UIRouter | null {
		return this.uiRouter ?? this.injector.get(UIRouter, null);
	}

	/**
	 * Initializes the application service: subscribes to events, starts WebSocket and inactivity checks.
	 */
	init(): void {
		if (this.isInitialized) {
				return;
		}

		const appData = this.appData.getData();

		this.subscribeToLogoutEvents();
		this.commonCheckInactiveService.start();

		if (!appData?.LinkToken && this.appConfig.shouldAuthorize) {
			this.initializeWebSocketConnection();
		}

		this.showAppStartNotification();
		this.isInitialized = true;
	}

	/**
	 * Subscribes to unauthorized access events to handle user logout.
	 */
	private subscribeToLogoutEvents(): void {
		this.commonEventsService.on(COMMON_USER_UNAUTHORIZED_EVENT)
			.pipe(
				take(1),
				takeUntil(this.destroy$),
			)
			.subscribe(() => {
				this.onLogoutHandler();
			});
	}

	/**
	 * Initializes WebSocket connection and subscribes to all required SignalR events.
	 */
	private initializeWebSocketConnection(): void {
		const signalREvents = this.getSignalREvents();
		this.subscribeToSignalREvents(signalREvents);
		this.subscribeToSingleUserNotifications();
		this.subscribeToOpenUserDialog();
		this.subscribeToUserBlocked();
		this.subscribeToOpenUrl();
		this.startWebSocketConnection();
	}

	/**
	 * Gets the list of SignalR events from application modules and returns a unique flattened array.
	 */
	private getSignalREvents(): string[] {
		if (!this.commonModulesPlaceholders) {
			return [];
		}

		const signalREvents = this.commonModulesPlaceholders.getPlaceholder<string[]>('SignalREvents');

		if (signalREvents && Array.isArray(signalREvents)) {
			const flattenedEvents = flatten(signalREvents);
			const uniqueEvents = [...new Set(flattenedEvents)];

			return uniqueEvents;
		}

		return [];
	}

	/**
	 * Subscribes to specified SignalR events and broadcasts them through the events service.
	 */
	private subscribeToSignalREvents(events: string[]): void {
		events.forEach(event => {
			const unsubscribe = this.commonWebSocketConnection.on(event, (...args: any[]) => {
				this.commonEventsService.emit({
					name: 'SignalR:' + event,
					payload: args,
				});
			});

			this.signalRSubscriptions.push({ unsubscribe } as any);
		});
	}

	/**
	 * Subscribes to personal notifications for the current user via SignalR.
	 */
	private subscribeToSingleUserNotifications(): void {
		const unsubscribe = this.commonWebSocketConnection.on('OnNotifySingleUserMethodCalled', (...args: any[]) => {
			const [title, body, type, buttons] = args;

			this.notificationService.show(title, body, type, {
				buttons: (buttons || []).map(btn => ({
					title: btn.Label,
					href: btn.Url,
					openInCurrentWindow: btn.InCurrentWindow,
					closeAfterClick: btn.CloseAfterClick,
				}))
			});
		});

		this.signalRSubscriptions.push({ unsubscribe } as any);
	}

	/**
	 * Subscribes to user notification dialogs via SignalR.
	 */
	private subscribeToOpenUserDialog(): void {
		const unsubscribe = this.commonWebSocketConnection.on('OnOpenUserDialog', (...args: any[]) => {
			const data = args as ICommonUserNotificationDialog[];
			this.commonUserNotificationDialogService.handleOpenDialog(data);
		});

		this.signalRSubscriptions.push({ unsubscribe } as any);
	}

	/**
	 * Subscribes to user blocked event and performs logout.
	 */
	private subscribeToUserBlocked(): void {
		const unsubscribe = this.commonWebSocketConnection.on('UserBlocked', (...args: any[]) => {
			this.onLogoutHandler();
		});

		this.signalRSubscriptions.push({ unsubscribe } as any);
	}

	/**
	 * Subscribes to open URL event and performs navigation in current or new window.
	 */
	private subscribeToOpenUrl(): void {
		const unsubscribe = this.commonWebSocketConnection.on('OpenUrl', (...args: any[]) => {
			const [url, id, inCurrentWindow] = args;
			const openInNewWindow = !inCurrentWindow;

			if (openInNewWindow) {
				this.commonWindowService.openWindowWithCheckId(url, id, true);
			} else {
				const router = this.getRouter();

				if (router) {
					if (url.startsWith('/') || url.includes('://')) {
						window.location.href = url;
					} else {
						router.stateService.go(url);
					}
				} else {
					this.commonWindowService.openWindowWithCheckId(url, id, true);
				}
			}
		});

		this.signalRSubscriptions.push({ unsubscribe } as any);
	}

	/**
	 * Starts WebSocket connection based on settings: regular or leader tab based.
	 */
	private startWebSocketConnection(): void {
		const appData = this.appData.getData();

		if (appData?.IsWebSocketsEnabled) {
			this.commonWebSocketConnection.start();
		} else {
			this.startLeaderBasedConnection();
		}
	}

	/**
	 * Starts WebSocket connection only on the leader tab to optimize resources.
	 */
	private startLeaderBasedConnection(): void {
		this.commonWindowService.startCheckingLeader();

		const subscription = this.commonWindowService.iAmLeader$
			.pipe(takeUntil(this.destroy$))
			.subscribe((iAmLeader) => {
				if (iAmLeader) {
					if (this.leaderCheckTimer) {
						cancelTask(this.leaderCheckTimer);
					}

					setCustomData({
							isStarting: true
					});

					this.commonWebSocketConnection.start()
						.finally(() => {
							setCustomData({
								isStarting: false
							});
						});
				} else {
					this.scheduleLeaderCheck();
				}
			});

		this.signalRSubscriptions.push({ unsubscribe: () => subscription.unsubscribe() } as any);
	}

	/**
	 * Schedules periodic leader tab status check and stops connection when leader is ready.
	 */
	private scheduleLeaderCheck(): void {
		const runCheck = () => {
			const leaders = getLeaders();
			const leader = getMainLeader(leaders);

			if (leader && leader.customData && leader.customData.isStarting === false) {
				this.commonWebSocketConnection.stop();
			} else {
				this.leaderCheckTimer = timeoutByIndexBD(() => runCheck(), 100);
			}
		};

		this.leaderCheckTimer = timeoutByIndexBD(() => runCheck(), 500);
	}

	/**
	 * Shows notification on application start if configured in application data.
	 */
	private showAppStartNotification(): void {
		const appData = this.appData.getData();

		if (appData?.appStartNotificationText) {
			setTimeout(() => {
				this.notificationService.show(
					appData.appStartNotificationText,
					'',
					'info'
				);
			}, 100);
		}
	}

	/**
	 * Handles user logout: redirects to login page or emits logout event.
	 */
	private async onLogoutHandler(): Promise<void> {
		const appData = this.appData.getData();

		if (!appData?.LinkToken) {
			try {
				await this.commonWebSocketConnection.stop();
			} catch (error) {
				console.warn('CommonWebSocketConnection did not stop', error);
			}
		}

		this.commonProfileService.goToLoginPage();
	}
}
